import { isObject } from '@mimir/primitive/type-guards/isObject';

export interface ResetPlayoutDto {
  janitorCommand: 'resetPlayout';
  keldaId: string;
}


export function isResetPlayoutDto(obj: unknown): obj is ResetPlayoutDto {
  if (!isObject(obj)) {
    return false;
  }
  return 'janitorCommand' in obj && obj.janitorCommand === 'resetPlayout'
    && 'keldaId' in obj && typeof obj.keldaId === 'string';
}


/**
 * Indicates that someone wants to take control over this playout server.
 */
export interface Lock {
  type: 'lock';
  lockedBy: LockOwner;
}


/**
 * Releases this playout server.
 */
export interface Unlock {
  type: 'unlock';
}


/**
 * Indicates that the playout server should terminate as soon as asked to
 * by the supervisor, without waiting for the device to be unlocked,
 * for instance if the device configuration was deleted.
 */
export interface Terminate {
  type: 'terminate';
  immediately: boolean;
}

export interface Cue {
  type: 'cue';
  itemId: string;
  firstFrameInSeconds: number;
  lastFrameInSeconds?: number;
}


export interface Play {
  type: 'play';
}


export interface Pause {
  type: 'pause';
}


export interface Eject {
  type: 'eject';
}

export interface Loop {
  type: 'toggleLoop';
}

export interface DebugInfo {
  type: 'toggleDebugInfo';
}

export type PlayoutCommand = Lock | Unlock | Cue | Play | Pause | Eject | Loop | Terminate | DebugInfo;


/**
 * The combination of these attributes defines the
 * identity of a playout device within a given Kelda configuration.
 */
export interface KeldaInternalPlayoutDeviceId {
  type: 'sdi' | 'ndi' | 'custom';

  /**
   * For an SDI device, this is the sink name reported by ffmpeg
   * like 'DeckLink Duo (1)'. For a custom device, it is an output
   * url like 'udp://127.0.0.1:1234'
   */
  value: string;

  /**
   * SDI cards are attached to physical machines but different Kelda
   * instances can run on many different machines using the same
   * Kelda configuration. We use the hostname of the container where
   * an SDI outputs was detected as a way to be able to distinguish which
   * Kelda instance can actually use this given SDI device.
   */
   hostname: string;
}


/**
 * This identifies a unique playout device for a given tenant.
 */
export interface PlayoutDeviceId extends KeldaInternalPlayoutDeviceId {
  keldaId: string;
}


/**
 * This interface describes a command to be sent to playout server.
 */
export interface KeldaPlayoutCommandDto {
  command: PlayoutCommand;
  playoutDeviceId: PlayoutDeviceId;
}


export interface PlayoutState {
  status: PlayoutDeviceStatus;
  previewUrl?: string;
  modifiedOn: string;
}

export interface PlayoutDeviceDto {
  playoutDeviceId: PlayoutDeviceId;
  displayName: string;

  /**
   * 'unknown' if there is no known status for this device.
   */
  state: PlayoutState | 'unknown';
}


export function getPlayoutDeviceKey(id: PlayoutDeviceId) {
  return JSON.stringify([id.keldaId, id.hostname, id.type, id.value]);
}


export const isKeldaPlayoutCommandDto = (dto: unknown): dto is KeldaPlayoutCommandDto => {
  if (!isObject(dto)) {
    return false;
  }
  return 'command' in dto
    && 'playoutDeviceId' in dto;
};


/**
 * The playout server has not opened the output device yet.
 * When all the SDI channels are configured in output mode,
 * the playout servers go very quickly from this state
 * to "unlocked". However, if some channels are configured for input,
 * the playout server will stay in this mode until the channel is re-configured
 * to be anc output channel.
 */
export interface Initializing {
  type: 'initializing';
}


/**
 * No one is using the playout device, it is up for grab.
 * When a device is in this state, it is outputing a default
 * content of our choosing, like a Mimir logo.
 */
export interface Unlocked {
  type: 'unlocked';
  debugInfo: boolean;
}


export interface LockOwner {
  /**
   * Mimir internal ID of the user that has locked the device
   */
  userId: string;

  /**
   * A display value describing the user.
   */
  description: string;
}

interface Locked {
  /**
   * User that has locked the device
   */
  lockedBy: LockOwner;

  /**
   * ISO representation of when this device was locked.
   */
  lockedDate: string;

  /**
   * If defined, the ID of the item currently being cued.
   */
  cueingItemId?: string;

  looping: boolean;

  debugInfo: boolean;

  cueError?: string;
}

/**
 * The device is locked by a user, but is not playing any content.
 * The device is output black frames with silence.
 */
export interface StandBy extends Locked {

  type: 'standby';

}


/**
 * The device is locked by a user and there is an item being rendered.
 */
export interface Active extends Locked {

  type: 'active';

  /**
   * The ID of the item being rendered.
   */
  currentItemId: string;

  /**
   * Whether the item is paused or being played.
   */
  paused: boolean;

  /**
   * The current position in ms from the beginning of the item's media.
   */
  positionInMs: number;

  /**
   * The currently configured mark-out. If not set the entire media is considered to be playing
   */
  markOutInMs?: number;

  /**
   * The currently configured mark-in.
   */
  markInInMs: number;
}

export type PlayoutDeviceStatus = Initializing | Unlocked | StandBy | Active;


export interface PlayoutDeviceStatusGetDto {
  playoutDeviceId: PlayoutDeviceId;
  status: PlayoutDeviceStatus;
  /**
   * ISO representation of when this status was last updated.
   */
  modifiedOn: string;

  /**
   * If defined, represents a url to an mpjpeg preview stream.
   */
  previewUrl?: string;
}


export type PlayoutDeviceStatusUpdateDto = Omit<PlayoutDeviceStatusGetDto, 'modifiedOn'>;


export const isPlayoutDeviceStatusUpdateDto = (dto: unknown): dto is PlayoutDeviceStatusUpdateDto => {
  if (!isObject(dto)) {
    return false;
  }
  return 'status' in dto && 'playoutDeviceId' in dto;
};


export function samePlayoutDeviceIds(a: PlayoutDeviceId, b: PlayoutDeviceId) {
  return a.type === b.type
    && a.value === b.value
    && a.hostname === b.hostname
    && a.keldaId === b.keldaId;
}


export function sameKeldaInternalPlayoutDeviceIds(a: KeldaInternalPlayoutDeviceId, b: KeldaInternalPlayoutDeviceId) {
  return a.type === b.type
    && a.value === b.value
    && a.hostname === b.hostname;
}
